'use client'

import { AnimatePresence, motion } from 'framer-motion'
import type { CSSProperties, PropsWithChildren } from 'react'
import React, { useEffect, useRef, useState } from 'react'

export interface Props {
    className?: string
    delay?: number
    direction?: 'up' | 'down' | 'auto'
    inline?: boolean
    style?: CSSProperties
    translateValue?: string
}

export default function AnimatedText(props: PropsWithChildren<Props>) {
    const { direction = 'auto', inline = false, delay = 0, className, style, translateValue = '100%', children } = props

    const prevValue = useRef<React.ReactNode | null>(null) // Store the previous children value
    const [calculatedDirection, setCalculatedDirection] = useState<'up' | 'down'>('up')
    const currentRef = useRef<HTMLDivElement>(null)

    // Calculate the direction if `auto` is specified
    useEffect(() => {
        if (direction === 'auto' && prevValue.current !== null) {
            const prev = parseFloat(prevValue.current as string)
            const next = parseFloat(children as string)

            if (!isNaN(prev) && !isNaN(next)) {
                setCalculatedDirection(next > prev ? 'up' : 'down')
            }
        }
        prevValue.current = children
    }, [children, direction])

    const actualDirection = direction === 'auto' ? calculatedDirection : direction
    const fromTransform = actualDirection === 'down' ? `-${translateValue}` : translateValue
    const leaveTransform = actualDirection === 'down' ? translateValue : `-${translateValue}`

    return (
        <motion.span
            className={className}
            style={{
                ...style,
                whiteSpace: inline ? 'nowrap' : 'normal',
                display: inline ? 'inline-flex' : 'flex',
                overflow: 'hidden', // Prevent visual glitches
            }}
        >
            <AnimatePresence initial={false}>
                <motion.span
                    key={children?.toString() ?? 'default'}
                    ref={currentRef}
                    initial={{
                        opacity: 0,
                        filter: 'blur(10px)',
                        y: fromTransform,
                    }}
                    animate={{
                        opacity: 1,
                        filter: 'blur(0)',
                        y: '0%',
                    }}
                    exit={{
                        opacity: 0,
                        y: leaveTransform,
                        position: 'absolute',
                    }}
                    transition={{
                        duration: 0.4,
                        ease: [0.2, 0.8, 0.2, 1],
                        delay: delay,
                    }}
                >
                    {children}
                </motion.span>
            </AnimatePresence>
        </motion.span>
    )
}
